import React from 'react';
import { BarChart3, TrendingUp, Award } from 'lucide-react';

const ComparativeAnalysisPanel = ({ departmentPerformance }) => {
  const getBestPerformer = () => {
    return departmentPerformance?.reduce((best, dept) => 
      dept?.efficiency > best?.efficiency ? dept : best
    , departmentPerformance?.[0]);
  };

  const bestPerformer = getBestPerformer();

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-purple-500/10 rounded-lg">
            <BarChart3 className="w-6 h-6 text-purple-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-foreground">Comparative Analysis</h2>
            <p className="text-sm text-muted-foreground">Departmental performance benchmarks and industry standards</p>
          </div>
        </div>

        {/* Best Performer Highlight */}
        <div className="bg-gradient-to-r from-success/10 to-success/5 border border-success/20 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <Award className="w-8 h-8 text-success" />
            <div>
              <p className="text-sm text-muted-foreground">Top Performing Department</p>
              <p className="text-xl font-bold text-foreground">{bestPerformer?.department}</p>
              <p className="text-sm text-success">Efficiency Score: {bestPerformer?.efficiency}%</p>
            </div>
          </div>
        </div>

        {/* Department Performance Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Department</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Onboarding Time</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Offboarding Time</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Efficiency Score</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Performance</th>
              </tr>
            </thead>
            <tbody>
              {departmentPerformance?.map((dept, index) => (
                <tr key={index} className="border-b border-border hover:bg-muted/50 transition-smooth">
                  <td className="py-3 px-4">
                    <span className="font-medium text-foreground">{dept?.department}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-muted-foreground">{dept?.onboardingTime} days</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-sm text-muted-foreground">{dept?.offboardingTime} days</span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full h-2 max-w-[100px]">
                        <div
                          className="bg-success h-2 rounded-full transition-all"
                          style={{ width: `${dept?.efficiency}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-foreground">{dept?.efficiency}%</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {dept?.efficiency >= 90 ? (
                      <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">Excellent</span>
                    ) : dept?.efficiency >= 85 ? (
                      <span className="px-2 py-1 bg-blue-500/10 text-blue-500 text-xs font-medium rounded-full">Good</span>
                    ) : (
                      <span className="px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full">Needs Improvement</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Industry Benchmark Comparison */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-muted-foreground">PNG Industry Avg</span>
            </div>
            <p className="text-xl font-bold text-foreground">16.2 days</p>
            <p className="text-xs text-muted-foreground">Onboarding duration</p>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              <span className="text-xs text-muted-foreground">Your Organization</span>
            </div>
            <p className="text-xl font-bold text-foreground">14.5 days</p>
            <p className="text-xs text-success">10.5% better than industry</p>
          </div>
          <div className="p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-muted-foreground">Best in Class</span>
            </div>
            <p className="text-xl font-bold text-foreground">12.0 days</p>
            <p className="text-xs text-muted-foreground">Target benchmark</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparativeAnalysisPanel;